/**
 * Client mirror of the Action Plan value-provenance contract (P0-4, P1-7).
 *
 * Types only, plus the one narrowing helper the UI needs. Client code cannot
 * import from `server/` without bundling server code, so the shapes are declared
 * twice and held identical by `client/types/__tests__/actionPlanValue.parity.test.ts`,
 * which fails to compile on any divergence. That parity test is the mechanism
 * that would have caught P0-3 and is the only thing making a second declaration
 * safe.
 *
 * Canonical definitions and all construction logic:
 * `server/services/actionPlanValueProvenance.ts`.
 */

/** Where a number came from, ordered from strongest to weakest claim. */
export type ValueProvenance =
  | 'observed'
  | 'user_assumption'
  | 'reference_assumption'
  | 'model_estimate'
  /** From a plan document stored before provenance existed — origin unrecoverable. */
  | 'legacy_unverified'
  | 'unavailable';

/** A number, or an honest admission that there isn't one. */
export interface Quantified<T> {
  value: T | null;
  provenance: ValueProvenance;
  basis?: string;
}

/** A plan-level warning, rendered rather than logged to the console. */
export interface PlanWarning {
  code: string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  field?: string;
}

/** FAIR pillar scores, each independently knowable or not. */
export interface FairScoresQuantified {
  F: number | null;
  A: number | null;
  I: number | null;
  R: number | null;
  overall: number | null;
  provenance: ValueProvenance;
  basis?: string;
}

/** ROI and payback, computed server-side so provenance travels with them. */
export interface DerivedFinancials {
  roi_pct: Quantified<number>;
  payback_months: Quantified<number>;
}

/**
 * True when the UI may render the number.
 *
 * Checks both halves of the invariant rather than trusting either alone: a
 * payload that arrived over the wire has not been through the server's
 * `assertQuantified`, and a client that only tested `value !== null` would render
 * a figure explicitly marked unavailable.
 */
export function hasValue<T>(
  q: Quantified<T> | null | undefined,
): q is Quantified<T> & { value: T } {
  return !!q && q.value !== null && q.provenance !== 'unavailable';
}

/**
 * How a provenance should be described to a reader.
 *
 * Kept beside the type so every surface says the same thing about the same
 * provenance. "Estimated" alone was the old label for values that were in fact
 * hardcoded placeholders, which is how a `?? 10000` became indistinguishable
 * from an analysis.
 */
export const PROVENANCE_LABEL: Record<ValueProvenance, string> = {
  observed: 'measured from your data',
  user_assumption: 'your assumption',
  reference_assumption: 'industry reference value',
  model_estimate: 'AI estimate',
  legacy_unverified: 'from an earlier plan version — source not recorded',
  unavailable: 'not available',
};
